import React, { FC } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import styled, { css } from 'styled-components';
import { colors } from '@ch/common/const/color';
import { spacingStyle, SpacingProps } from '@ch/common/const/spacing';
import { form, formControlSizeVariant, FormDefault } from '@ch/common/const/form-element';
import { font } from '@ch/common/const/font';
import { FormSelectProps } from './types';
import { FormInputBasicStyles } from '../input';
import { IconStyled } from '@ch/common/components/icon';
import { Box } from '@ch/common/components/box';
import { DefaultVirtualListItemHeight } from './virtual-list';

export const FormSelectContainerStyled = styled('div')<SpacingProps>`
  ${props => spacingStyle(props)};
`;

export const FormSelectChild: FC<FormSelectProps> = props =>
  props.isCreatable ? <CreatableSelect {...props} /> : <Select {...props} />;

export const ColorMarkStyled = styled('div')`
  display: block;
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid ${colors.grayMedium};
`;

export const FormMobileSelectStyled = styled('div')<{ hideError?: boolean; colorMark?: string }>`
  position: relative;
  width: 100%;
  background: ${colors.white};

  ${ColorMarkStyled} {
    background-color: ${({ colorMark }) => colorMark};
  }

  ${IconStyled} {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
  }

  > select {
    height: ${form?.input['sm']?.height}px;
    ${formControlSizeVariant('md')};
    ${FormInputBasicStyles};
    padding-right: 40px;
    ${({ colorMark }) => colorMark && 'padding-left: 45px'};
    position: relative;
    z-index: 1;
    background: transparent;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${({ hideError }) =>
      !hideError &&
      `
      &.is-invalid {
        margin-bottom: 30px;
      }
      + .invalid-feedback {
        position: absolute;
        bottom: -24px;
        left: 0;
      }
    `}
  }
`;

const colorMarkStyle = (colorMark: string) => css`
  &__single-value {
    display: flex;
    align-items: center;
    margin-left: 0;

    &:before {
      background-color: ${colorMark};
      border-radius: 50%;
      content: '';
      display: block;
      margin-right: 12px;
      height: 18px;
      width: 18px;
      border: 1px solid ${colors.grayMedium};
      flex: 1 0 18px;
    }
  }
  &__input {
    margin-left: 25px;
  }
`;

export const FormSelectWrapStyled = styled(Box)<{ $disabled?: boolean }>`
  ${({ $disabled }) => $disabled && 'cursor: not-allowed;'}
`;
export const FormSelectStyled = styled(FormSelectChild)<FormSelectProps>`
  ${ColorMarkStyled} {
    left: 0;
    background-color: ${({ colorMark }) => colorMark};
  }

  .react-select,
  .react-select-creatable {
    &__control {
      border-radius: 4px;
      border: ${({ error }) => `1px solid ${error ? colors.error : colors.grayMedium}`};
      line-height: 20px;
      min-height: ${form?.input['md']?.height}px;
      color: ${FormDefault.controlInputColor};
      box-sizing: border-box;
      font-size: ${font.size.md}px;
      &:hover {
        box-shadow: none;
        outline: none;
        border: 1px solid ${colors.grayMedium};
      }
      &--menu-is-open,
      &--is-focused {
        box-shadow: inset 0 0 0 1px rgba(61, 178, 215, 0.8);
        outline: none;
        border: 1px solid #3e9dbb;
      }
      &--is-disabled {
        color: ${FormDefault.controlInputColorDisabled};
        border-color: ${FormDefault.controlInputColorDisabled};
        background-color: ${FormDefault.controlInputBgColor};
        cursor: not-allowed;
        .react-select__dropdown-indicator ${IconStyled} {
          fill: ${colors.grayMedium} !important;
        }
      }
    }
    &__value-container {
      min-height: 30px;
      padding: 0 12px;
    }
    &__indicator {
      width: 40px;
      height: 30px;
      box-sizing: border-box;
      position: relative;

      &-separator {
        display: none;
      }
    }
    &__clear-indicator {
      width: auto;
      padding: 4px;
      cursor: pointer;
      &:hover {
        color: ${colors.white};
      }
    }
    &__dropdown-indicator-inner {
      width: 20px;
      height: 20px;
      text-align: center;
      position: absolute;
      top: 50%;
      margin: -10px 0 0;
    }
    &__menu {
      margin: -1px 0 0;
      border-radius: 0;
      z-index: 11;
      box-shadow: none;
      padding: 0;
      border: 1px solid ${colors.grayMedium};
      &-notice {
        font-size: ${font.size.xs}px;
      }
      &-list {
        padding: 0;
      }
    }
    &__multi-value {
      background-color: ${colors.grayDarker};
      &__label {
        height: 22px;
        font-size: ${font.size.xs}px;
        padding: 3px 8px;
        line-height: 16px;
        color: white;
        font-weight: 500;
      }
      &__remove {
        width: 32px;
        background-color: ${colors.grayDarker};
        color: white;
        cursor: pointer;
        text-align: center;
        align-items: center;
        justify-content: center;
        svg {
          margin: 0 auto;
        }

        &:hover,
        &:focus {
          background-color: ${colors.grayDark};
        }
      }
    }
    &__clear-btn {
      width: 22px;
      height: 22px;
      background-color: ${colors.primaryLighter};
      border: none;
      padding: 6px;
      line-height: 8px;
      cursor: pointer;
      &:hover,
      &:focus {
        outline: none;
        background-color: ${colors.primaryLight};
      }
    }
    &__no-result {
      height: ${DefaultVirtualListItemHeight}px;
      padding: 10px 15px;
      line-height: 24px;
    }
    &__option {
      height: ${DefaultVirtualListItemHeight}px;
      font-size: ${font.size.xs}px;
      cursor: pointer;
      position: relative;
      padding: 10px 15px;
      line-height: 24px;
      color: ${colors.primary};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .react-select__option .react-select-creatable__check-block,
  .react-select-creatable__option .react-select__check-ico {
    display: none;
  }
  .react-select {
    ${({ colorMark }) => colorMark && colorMarkStyle(colorMark)}

    &__single-value, &__input {
      color: ${FormDefault?.controlInputColor};
    }

    &__option {
      padding-left: 15px;
      padding-right: 45px;
      .react-select__check-ico {
        position: absolute;
        display: none;
        top: 40%;
        margin: -8px 0 0;
        right: 15px;
      }
      &.react-select__option--is-focused {
        background: inherit;
      }
      &:hover {
        background-color: ${colors.primary};
        color: ${colors.white};
      }
      &.react-select__option--is-selected {
        background-color: rgba(30, 40, 70, 0.12);
        border: 1px solid ${colors.primary};
        color: ${colors.primary};
        cursor: default;
        position: relative;
        .react-select__check-ico {
          display: block;
        }
      }
    }
  }
  .react-select__option.react-select__location-wrap {
    padding-left: 35px;
    .react-select__location-icon {
      display: block;
      position: absolute;
      top: 50%;
      left: 15px;
      margin: -11px 0 0;
    }
    &.react-select__option--is-focused,
    &.react-select__option--is-selected {
      background-color: ${colors.white};
      color: ${colors.primaryDark};
      * {
        fill: ${colors.primaryDark};
      }
    }
  }
  .react-select-creatable {
    &__option {
      padding-left: 48px;
      cursor: pointer;
      .react-select-creatable__check-block {
        width: 18px;
        height: 18px;
        border: 1px solid ${colors.grayMedium};
        top: 50%;
        left: 16px;
        display: block;
        position: absolute;
        margin: -9px 0 0;
        padding: 2px;
        svg {
          margin: 2px 0 0 0;
          display: none;
        }
      }
      &.react-select-creatable__option--is-focused,
      &.react-select-creatable__option--is-selected {
        background-color: ${colors.white};
      }
      &.react-select-creatable__option--is-selected {
        .react-select-creatable__check-block {
          svg {
            display: block;
          }
        }
      }
    }
  }
`;
